// React authentication context and hooks
import { createContext, useContext, useReducer, useCallback, useEffect, ReactNode } from 'react';
import { FirebaseAuthService } from './firebase';
import {
  AuthContextType,
  AuthState,
  User,
  LoginCredentials,
  SignupCredentials,
  AuthProvider as AuthProviderType,
  PasswordResetRequest,
  UserProfileUpdate,
  AuthResult,
} from './types';
import { AuthError } from './errors';

// Initial state
const initialState: AuthState = {
  user: null,
  loading: true,
  error: null,
};

// Action types
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_ERROR'; payload: AuthError | null }
  | { type: 'CLEAR_ERROR' };

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_USER':
      return { ...state, user: action.payload, loading: false };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

// Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider props
interface AuthProviderProps {
  children: ReactNode;
  authService: FirebaseAuthService;
}

// Provider component
export function AuthProvider({ children, authService }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Set loading state
  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  }, []);

  // Set user
  const setUser = useCallback((user: User | null) => {
    dispatch({ type: 'SET_USER', payload: user });
  }, []);

  // Set error
  const setError = useCallback((error: AuthError | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // Login
  const login = useCallback(
    async (credentials: LoginCredentials): Promise<AuthResult> => {
      setLoading(true);
      clearError();

      try {
        const result = await authService.login(credentials);

        if (result.success && result.user) {
          setUser(result.user);
        } else if (result.error) {
          setError(result.error);
        }

        return result;
      } catch (error) {
        console.error('Login error:', error);
        const authError = new AuthError('auth/unknown-error', 'An unexpected error occurred');
        setError(authError);
        return { success: false, error: authError };
      }
    },
    [authService, setLoading, setUser, setError, clearError],
  );

  // Signup
  const signup = useCallback(
    async (credentials: SignupCredentials): Promise<AuthResult> => {
      setLoading(true);
      clearError();

      try {
        const result = await authService.signup(credentials);

        if (result.success && result.user) {
          setUser(result.user);
        } else if (result.error) {
          setError(result.error);
        }

        return result;
      } catch (error) {
        console.error('Signup error:', error);
        const authError = new AuthError('auth/unknown-error', 'An unexpected error occurred');
        setError(authError);
        return { success: false, error: authError };
      }
    },
    [authService, setLoading, setUser, setError, clearError],
  );

  // Logout
  const logout = useCallback(async (): Promise<AuthResult> => {
    setLoading(true);
    clearError();

    try {
      const result = await authService.logout();

      if (result.success) {
        setUser(null);
      } else if (result.error) {
        setError(result.error);
      }

      return result;
    } catch (error) {
      console.error('Logout error:', error);
      const authError = new AuthError('auth/unknown-error', 'An unexpected error occurred');
      setError(authError);
      return { success: false, error: authError };
    }
  }, [authService, setLoading, setUser, setError, clearError]);

  // Login with provider
  const loginWithProvider = useCallback(
    async (provider: AuthProviderType): Promise<AuthResult> => {
      setLoading(true);
      clearError();

      try {
        const result = await authService.loginWithProvider(provider);

        if (result.success && result.user) {
          setUser(result.user);
        } else if (result.error) {
          setError(result.error);
        }

        return result;
      } catch (error) {
        console.error('Provider login error:', error);
        const authError = new AuthError('auth/unknown-error', 'An unexpected error occurred');
        setError(authError);
        return { success: false, error: authError };
      }
    },
    [authService, setLoading, setUser, setError, clearError],
  );

  // Reset password
  const resetPassword = useCallback(
    async (request: PasswordResetRequest): Promise<AuthResult> => {
      setLoading(true);
      clearError();

      try {
        const result = await authService.resetPassword(request);

        if (result.error) {
          setError(result.error);
        }

        return result;
      } catch (error) {
        console.error('Password reset error:', error);
        const authError = new AuthError('auth/unknown-error', 'An unexpected error occurred');
        setError(authError);
        return { success: false, error: authError };
      }
    },
    [authService, setLoading, setError, clearError],
  );

  // Update profile
  const updateProfile = useCallback(
    async (updates: UserProfileUpdate): Promise<AuthResult> => {
      setLoading(true);
      clearError();

      try {
        const result = await authService.updateProfile(updates);

        if (result.success && result.user) {
          setUser(result.user);
        } else if (result.error) {
          setError(result.error);
        }

        return result;
      } catch (error) {
        console.error('Profile update error:', error);
        const authError = new AuthError('auth/unknown-error', 'An unexpected error occurred');
        setError(authError);
        return { success: false, error: authError };
      }
    },
    [authService, setLoading, setUser, setError, clearError],
  );

  // Save user bio and complete onboarding
  const saveUserBioAndCompleteOnboarding = useCallback(
    async (userBio: any): Promise<AuthResult> => {
      setLoading(true);
      clearError();

      try {
        const result = await authService.saveUserBioAndCompleteOnboarding(userBio);

        if (result.error) {
          setError(result.error);
        }

        return result;
      } catch (error) {
        console.error('Save user bio error:', error);
        const authError = new AuthError('auth/unknown-error', 'An unexpected error occurred');
        setError(authError);
        return { success: false, error: authError };
      }
    },
    [authService, setLoading, setError, clearError],
  );

  // Set up auth state listener
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged((user) => {
      setUser(user);
    });

    return unsubscribe;
  }, [authService, setUser]);

  // Context value
  const value: AuthContextType = {
    user: state.user,
    loading: state.loading,
    error: state.error,
    login,
    signup,
    logout,
    loginWithProvider,
    resetPassword,
    updateProfile,
    saveUserBioAndCompleteOnboarding,
    clearError,
    isAuthenticated: !!state.user,
    getApp: () => authService.getApp(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook to use auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
